import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

import { apiClient } from '../../../services/api';

interface Props {
  /** Asset URL (signed or the plain `/assets/files/{id}` path). */
  url: string;
  alt?: string;
}

/**
 * Renders an asset image that may require auth to load. Native `<img>` loads
 * can't attach the `x-user-id` / Authorization headers the v3 backend needs,
 * and signed URLs aren't always present (e.g. local dev), so we fetch the
 * bytes through the authenticated `apiClient` and render an object URL. Mirrors
 * the blob-download pattern in `assetService.downloadImage` /
 * `AssetPreviewModal`. Re-fetches on `url` change and revokes the object URL on
 * cleanup so reloaded threads work and we don't leak blob handles.
 */
export const AuthedImage: React.FC<Props> = ({ url, alt = 'Generated image' }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    setObjectUrl(null);
    setFailed(false);
    apiClient
      .get(url, { responseType: 'blob' })
      .then(res => {
        if (cancelled) return;
        created = URL.createObjectURL(res.data as Blob);
        setObjectUrl(created);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [url]);

  if (failed) {
    return (
      <Typography variant="caption" color="error.main">
        Image unavailable
      </Typography>
    );
  }
  if (!objectUrl) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src={objectUrl}
      alt={alt}
      sx={{ display: 'block', maxWidth: '100%', maxHeight: 300, borderRadius: 1 }}
    />
  );
};
